interface SchemaMarkup {
  '@context': string;
  '@type'?: string;
  '@graph'?: unknown[];
  '@id'?: string;
  itemListElement?: unknown[];
  [key: string]: unknown;
}

const schemas: Record<string, SchemaMarkup> = {
  '/': {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://heraflight.com/#organization',
        name: 'Hera Flight',
        url: 'https://heraflight.com/',
        description:
          'Private jet company offering charter flights, jet card memberships, aircraft management, and aircraft sales & acquisitions.',
        email: 'contact@heraflight.com',
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'Charter Quotes',
            email: 'charter@heraflight.com',
            telephone: '1-833-FLY-HERA',
            areaServed: 'Worldwide',
            availableLanguage: ['en'],
          },
          {
            '@type': 'ContactPoint',
            contactType: 'Inquiries & Sales',
            email: 'sales@heraflight.com',
            telephone: '1-833-FLY-HERA',
            areaServed: 'Worldwide',
            availableLanguage: ['en'],
          },
        ],
        address: {
          '@type': 'PostalAddress',
          streetAddress: '15965 Fairchild Dr',
          addressLocality: 'Clearwater',
          addressRegion: 'FL',
          postalCode: '33762',
          addressCountry: 'US',
        },
        /* If you have a logo image URL, add:
        ,
        "logo": {
          "@type": "ImageObject",
          "url": "https://heraflight.com/path-to-logo.png"
        }
        */
        /* If you have public social profiles, add:
        ,
        "sameAs": [
          "https://www.instagram.com/yourhandle",
          "https://www.facebook.com/yourpage",
          "https://www.linkedin.com/company/yourcompany",
          "https://twitter.com/yourhandle"
        ]
        */
      },

      {
        '@type': 'TravelAgency',
        '@id': 'https://heraflight.com/#localbusiness',
        name: 'Hera Flight',
        url: 'https://heraflight.com/',
        image: 'https://heraflight.com/',
        telephone: '1-833-FLY-HERA',
        priceRange: '$$$$',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '15965 Fairchild Dr',
          addressLocality: 'Clearwater',
          addressRegion: 'FL',
          postalCode: '33762',
          addressCountry: 'US',
        },
        areaServed: [
          { '@type': 'Country', name: 'United States' },
          { '@type': 'AdministrativeArea', name: 'International' },
        ],
        parentOrganization: { '@id': 'https://heraflight.com/#organization' },
      },

      {
        '@type': 'WebSite',
        '@id': 'https://heraflight.com/#website',
        url: 'https://heraflight.com/',
        name: 'Hera Flight',
        inLanguage: 'en',
        publisher: { '@id': 'https://heraflight.com/#organization' },
        /* If you add an on-site search, include:
        ,
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://heraflight.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
        */
      },

      {
        '@type': 'WebPage',
        '@id': 'https://heraflight.com/#webpage',
        url: 'https://heraflight.com/',
        name: 'Private Jet Company | Charter Flights, Rental, Management & Jet Card Membership',
        isPartOf: { '@id': 'https://heraflight.com/#website' },
        inLanguage: 'en',
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: 'https://heraflight.com/',
        },
        about: { '@id': 'https://heraflight.com/#organization' },
        breadcrumb: { '@id': 'https://heraflight.com/#breadcrumb' },
        /* Optionally add dates if you maintain them for the homepage:
        ,
        "datePublished": "2025-07-25",
        "dateModified": "2025-07-25"
        */
      },

      {
        '@type': 'BreadcrumbList',
        '@id': 'https://heraflight.com/#breadcrumb',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://heraflight.com/',
          },
        ],
      },

      /* === Services === */
      {
        '@type': 'Service',
        '@id': 'https://heraflight.com/#service-charter',
        name: 'Private Jet Charters',
        serviceType: 'On-demand private jet charter',
        provider: { '@id': 'https://heraflight.com/#organization' },
        areaServed: 'Worldwide',
        description:
          'Dynamic pricing across 22,000+ airport pairs in the U.S. and international destinations on a flight-by-flight basis.',
      },

      {
        '@type': 'Service',
        '@id': 'https://heraflight.com/#service-jetcard',
        name: 'Jet Card Membership',
        serviceType: 'Jet card',
        provider: { '@id': 'https://heraflight.com/#organization' },
        areaServed: 'Worldwide',
        description:
          'Guaranteed availability, guaranteed hourly rates, and the highest safety standards across an exclusive fleet.',
      },

      {
        '@type': 'Service',
        '@id': 'https://heraflight.com/#service-management',
        name: 'Aircraft Management',
        serviceType: 'Aircraft management',
        provider: { '@id': 'https://heraflight.com/#organization' },
        areaServed: 'Worldwide',
        description:
          'End-to-end management including pilots, hangar, fuel, maintenance, insurance, with guaranteed charter revenue to offset ownership costs.',
      },

      {
        '@type': 'Service',
        '@id': 'https://heraflight.com/#service-sales',
        name: 'Aircraft Sales & Acquisitions',
        serviceType: 'Aircraft brokerage',
        provider: { '@id': 'https://heraflight.com/#organization' },
        areaServed: 'Worldwide',
        description:
          'Expert guidance to buy and sell the right jet with proper due diligence, preserving equity on purchase and resale.',
      },

      /* === Offer Catalog that groups your services === */
      {
        '@type': 'OfferCatalog',
        '@id': 'https://heraflight.com/#catalog-services',
        name: 'Hera Flight Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@id': 'https://heraflight.com/#service-charter' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@id': 'https://heraflight.com/#service-jetcard' },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@id': 'https://heraflight.com/#service-management',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@id': 'https://heraflight.com/#service-sales' },
          },
        ],
        url: 'https://heraflight.com/',
      },

      /* === If you later publish specific “Available Fleet” detail pages, add Product/Vehicle entries per aircraft and link them here via additional Offers. === */
    ],
  },
  '/contact': {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://heraflight.com/#organization',
        name: 'Hera Flight',
        url: 'https://heraflight.com/',
        description:
          'Private jet company offering charter flights, jet card memberships, aircraft management, and aircraft sales & acquisitions.',
        email: 'contact@heraflight.com',
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'Charter Quotes',
            email: 'charter@heraflight.com',
            telephone: '1-833-FLY-HERA',
            areaServed: 'Worldwide',
            availableLanguage: ['en'],
          },
          {
            '@type': 'ContactPoint',
            contactType: 'Inquiries & Sales',
            email: 'sales@heraflight.com',
            telephone: '1-833-FLY-HERA',
            areaServed: 'Worldwide',
            availableLanguage: ['en'],
          },
        ],
        address: {
          '@type': 'PostalAddress',
          streetAddress: '15965 Fairchild Dr',
          addressLocality: 'Clearwater',
          addressRegion: 'FL',
          postalCode: '33762',
          addressCountry: 'US',
        },
      },

      {
        '@type': 'WebSite',
        '@id': 'https://heraflight.com/#website',
        url: 'https://heraflight.com/',
        name: 'Hera Flight',
        inLanguage: 'en',
        publisher: { '@id': 'https://heraflight.com/#organization' },
      },

      {
        '@type': 'ContactPage',
        '@id': 'https://heraflight.com/contact#webpage',
        url: 'https://heraflight.com/contact',
        name: 'Contact Hera Flight',
        isPartOf: { '@id': 'https://heraflight.com/#website' },
        inLanguage: 'en',
        about: { '@id': 'https://heraflight.com/#organization' },
        mainEntity: { '@id': 'https://heraflight.com/#organization' },
        breadcrumb: { '@id': 'https://heraflight.com/contact#breadcrumb' },
      },

      {
        '@type': 'BreadcrumbList',
        '@id': 'https://heraflight.com/contact#breadcrumb',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://heraflight.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contact',
            item: 'https://heraflight.com/contact',
          },
        ],
      },
    ],
  },
  '/about': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'About',
        item: 'https://heraflight.com/about',
      },
    ],
  },
  '/management': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Management',
        item: 'https://heraflight.com/management',
      },
    ],
  },
  '/jobs': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Jobs',
        item: 'https://heraflight.com/jobs',
      },
    ],
  },
  '/hera-flight-race-team': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Hera Flight Race Team',
        item: 'https://heraflight.com/hera-flight-race-team',
      },
    ],
  },
  '/aircraft-management': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Aircraft Management',
        item: 'https://heraflight.com/aircraft-management',
      },
    ],
  },
  '/aircraft-sales': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Aircraft Sales',
        item: 'https://heraflight.com/aircraft-sales',
      },
    ],
  },
  '/charter': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Charter',
        item: 'https://heraflight.com/charter',
      },
    ],
  },
  '/empty-leg-flights': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Empty Leg Flights',
        item: 'https://heraflight.com/empty-leg-flights',
      },
    ],
  },
  '/hera-jet-card-membership': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Hera Jet Card Membership',
        item: 'https://heraflight.com/hera-jet-card-membership',
      },
    ],
  },
  '/available-fleet': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Available Fleet',
        item: 'https://heraflight.com/available-fleet',
      },
    ],
  },
  '/blog': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://heraflight.com/blog',
      },
    ],
  },
  '/services': {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://heraflight.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Services',
        item: 'https://heraflight.com/services',
      },
    ],
  },
};

export const getSchemaMarkup = (pageId: string): SchemaMarkup => {
  if (schemas.hasOwnProperty(pageId)) {
    return schemas[pageId];
  } else {
    return schemas['/'];
  }
};
